import React from 'react'
import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import AppHeader from '~/client/components/AppHeader'
import { useAuthStore } from '~/client/common/stores/auth'
import { supabaseClient } from '~/server/common/supabase/client'
import { sanitizeRedirectPath } from '~/shared/common/redirect-path'
import Gleap from 'gleap'
import { initGleap } from '~/client/common/gleap'
import { hasFeature, FeatureFlag } from '~/client/common/features'
import { AGREEMENT_CONSENT_ENABLED } from '~/client/common/envvars'
import { useVenueTagGroups } from '~/client/venue-tag/venue-tag.hooks'
import { ConsentRequiredModal } from '~/client/components/agreement/ConsentRequiredModal'
import { activeAgreementsQuery, missingConsentsQuery } from '~/client/agreement/agreement.queries'
import { Button } from '~/client/components/ui/button'

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    // Avoid server-side redirects where the Supabase session is not available (SSR)
    if (typeof window === 'undefined') return

    const captured = sanitizeRedirectPath(location.href)
    const search = captured ? { redirect: captured } : undefined

    // Check if we have a valid session - this will attempt to refresh if expired
    const {
      data: { session },
      error,
    } = await supabaseClient.auth.getSession()

    console.log('[Auth Debug] getSession result:', {
      hasSession: !!session,
      user: session?.user?.email,
      userId: session?.user?.id,
      expiresAt: session?.expires_at,
      error,
    })

    // If there's an error or no session (token expired and couldn't refresh), redirect to login
    if (error || !session) {
      console.log('[Auth Debug] No session or error, redirecting to login', { error })
      // Clear any stale auth state, including impersonation fields so a server-side
      // session left alive (up to 2hr hard-expiry) doesn't surface on next login.
      useAuthStore.setState({
        user: null,
        userDetail: null,
        viewAsUser: null,
        viewAsUserDetail: null,
        impersonationToken: null,
        impersonationExpiresAt: null,
        initialized: true,
      })
      throw redirect({ to: '/login', search })
    }

    // Initialize auth store with valid session
    await useAuthStore.getState().initializeAuth()

    if (!useAuthStore.getState().user) {
      throw redirect({ to: '/login', search })
    }
  },
  component: RouteComponent,
})

// Routes that should show a minimal header (hide venue group selector and events)
const MINIMAL_HEADER_ROUTES = ['/reset-password', '/change-password']

function RouteComponent() {
  const navigate = Route.useNavigate()
  const location = useLocation()
  const isMinimalHeader = MINIMAL_HEADER_ROUTES.some((route) => location.pathname.includes(route))

  // Session-level prefetch of venue-tag groups: the badge data is entity-scoped
  // and auth lives in localStorage (not a cookie), so it can't ride the SSR
  // loader. Calling the hook here fires the request the moment the _app shell
  // mounts, in parallel with whatever the child route is loading — that way
  // tag pills don't lag behind SSR'd content on the space/listing pages.
  const userDetail = useAuthStore((s) => s.userDetail)
  useVenueTagGroups({ enabled: hasFeature(userDetail, FeatureFlag.VENUE_TAGS) })

  // Agreement consent gate. Both queries run in parallel. We must NOT render
  // <Outlet /> while these are pending — otherwise the user can briefly
  // interact with an app page before the modal mounts. We only block while
  // authReady is true (the auth-init phase is already handled by beforeLoad).
  const authReady = useAuthStore((s) => s.initialized && !!s.user)
  const missingQuery = useQuery({ ...missingConsentsQuery(), enabled: authReady && AGREEMENT_CONSENT_ENABLED })
  const activeQuery = useQuery({ ...activeAgreementsQuery(), enabled: authReady && AGREEMENT_CONSENT_ENABLED })
  const missingTypes = missingQuery.data
  const activeVersions = activeQuery.data
  // Fail closed: if either consent query errors, block the app behind a
  // retry overlay instead of letting the user through without the modal.
  // authReady is intentionally not part of this — the auth-init phase is
  // already handled by beforeLoad and should not surface this overlay.
  const consentCheckErrored =
    AGREEMENT_CONSENT_ENABLED && authReady && (missingQuery.isError || activeQuery.isError)
  // When the feature is off the queries are disabled and stay isPending, so the
  // flag must short-circuit to resolved — otherwise the full-page spinner hangs.
  const consentCheckResolved =
    !AGREEMENT_CONSENT_ENABLED || !authReady || (!missingQuery.isPending && !activeQuery.isPending)
  const showConsentModal =
    AGREEMENT_CONSENT_ENABLED &&
    authReady &&
    consentCheckResolved &&
    !consentCheckErrored &&
    (missingTypes?.length ?? 0) > 0 &&
    activeVersions !== undefined

  const handleRetryConsentCheck = () => {
    if (missingQuery.isError) void missingQuery.refetch()
    if (activeQuery.isError) void activeQuery.refetch()
  }

  React.useEffect(() => {
    if ((import.meta.env.VITE_ENV_ROLE ?? 'preview') === 'production') {

      initGleap()
      Gleap.showFeedbackButton(true)
      return () => {
        Gleap.showFeedbackButton(false)
      }
    }

  }, [])

  // Listen for session expiration during active use
  React.useEffect(() => {
    // Ensure auth is initialized on client mount (in case beforeLoad was skipped on server)
    useAuthStore.getState().initializeAuth().catch(console.error)

    const captureCurrent = () => {
      const captured = sanitizeRedirectPath(window.location.pathname + window.location.search + window.location.hash)
      return captured ? { redirect: captured } : {}
    }

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event, session) => {

      // Handle initial session check (fixes issue where SSR skips beforeLoad check)
      if (event === 'INITIAL_SESSION' && !session) {
        console.log('[Auth Debug] No initial session, redirecting')
        useAuthStore.setState({
          user: null,
          userDetail: null,
          viewAsUser: null,
          viewAsUserDetail: null,
          impersonationToken: null,
          impersonationExpiresAt: null,
        })
        void navigate({ to: '/login', search: captureCurrent() })
      }

      // Redirect to login if user is signed out or session becomes invalid.
      // Note: the admin's Supabase session is never destroyed during impersonation,
      // so SIGNED_OUT will not fire during an active impersonation session.
      if (event === 'SIGNED_OUT') {
        console.log('[Auth Debug] User signed out, redirecting')
        useAuthStore.setState({
          user: null,
          userDetail: null,
          viewAsUser: null,
          viewAsUserDetail: null,
          impersonationToken: null,
          impersonationExpiresAt: null,
        })
        navigate({ to: '/login' })
      } else if (event === 'TOKEN_REFRESHED' && !session) {
        console.log('[Auth Debug] Token refresh failed, redirecting')
        // Token refresh was attempted but failed - session is invalid
        useAuthStore.setState({
          user: null,
          userDetail: null,
          viewAsUser: null,
          viewAsUserDetail: null,
          impersonationToken: null,
          impersonationExpiresAt: null,
        })
        void navigate({ to: '/login', search: captureCurrent() })
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [navigate])

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <AppHeader showVenueGroupSelector={!isMinimalHeader} showEvents={!isMinimalHeader} showAccountMenu={!isMinimalHeader} />
      <div className="w-full h-full flex-1 min-h-0 overflow-y-auto">
        {consentCheckErrored ? (
          // Fail-closed overlay: if the consent check itself failed we
          // cannot know whether the user owes consent, so block the app
          // and let them retry rather than rendering <Outlet />.
          <div className="w-full h-full flex items-center justify-center p-4">
            <div className="max-w-md w-full rounded-lg border bg-card text-card-foreground shadow-sm p-6 flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-semibold">We couldn&apos;t verify your agreements</h2>
                <p className="text-sm text-muted-foreground">
                  Something went wrong while checking whether you need to accept any
                  updated agreements. Please try again.
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleRetryConsentCheck}
                  disabled={missingQuery.isFetching || activeQuery.isFetching}
                >
                  {missingQuery.isFetching || activeQuery.isFetching ? 'Retrying…' : 'Try again'}
                </Button>
              </div>
            </div>
          </div>
        ) : consentCheckResolved ? (
          <Outlet />
        ) : (
          // Full-page blocking overlay while we resolve whether the user
          // needs to consent — prevents a flicker/interaction window where
          // the app page is mounted before the modal appears.
          <div className="w-full h-full flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#8A3DFF] border-t-transparent" />
          </div>
        )}
      </div>
      {showConsentModal && missingTypes && activeVersions && (
        <ConsentRequiredModal missingTypes={missingTypes} activeVersions={activeVersions} />
      )}
    </div>
  )
}
